import { useQueryClient } from "@tanstack/react-query";
import type { PaymentEscalationState } from "../api/queries/escalationQueries";
import { escalationKeys } from "../api/queries/escalationQueries";
import { EscalationApprovalBanner } from "./EscalationApprovalBanner";
import { displayISODate } from "../utils/display";
import type { EmailDraft } from "../utils/interfaces";

function formatEntryReason(reason: string): string {
  return reason.replaceAll("_", " ");
}

function EscalationEventTimeline({
  events,
}: {
  events: PaymentEscalationState["events"];
}) {
  if (events.length === 0) {
    return (
      <p className="text-sm text-slate-500">No escalation steps recorded yet.</p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-left text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <th className="px-2 py-2 font-medium">Step</th>
            <th className="px-2 py-2 font-medium">Status</th>
            <th className="px-2 py-2 font-medium">Recipients</th>
            <th className="px-2 py-2 font-medium">Date</th>
          </tr>
        </thead>
        <tbody>
          {events.map((event) => (
            <tr key={event.id} className="border-b border-slate-100">
              <td className="px-2 py-2 text-slate-900">
                {event.level_name ?? "—"}
              </td>
              <td className="px-2 py-2 capitalize text-slate-700">
                {event.status.replaceAll("_", " ")}
              </td>
              <td className="max-w-[12rem] truncate px-2 py-2 text-slate-600">
                {(event.recipients ?? []).join(", ") || "—"}
              </td>
              <td className="whitespace-nowrap px-2 py-2 text-slate-600">
                {event.executed_at
                  ? displayISODate(event.executed_at)
                  : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EscalationDraftPreview({ draft }: { draft: EmailDraft }) {
  const recipients = (draft.to ?? []).join(", ");
  const bodyExcerpt = (draft.text ?? "").replace(/<[^>]+>/g, "").trim();
  const excerpt =
    bodyExcerpt.length > 180
      ? `${bodyExcerpt.slice(0, 180)}…`
      : bodyExcerpt;

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm">
      <p className="font-medium text-slate-900">Pending draft</p>
      <p className="mt-1 text-slate-700">
        <span className="font-medium">Subject:</span> {draft.subject || "—"}
      </p>
      <p className="mt-1 text-slate-700">
        <span className="font-medium">To:</span> {recipients || "—"}
      </p>
      {excerpt ? (
        <p className="mt-2 whitespace-pre-wrap text-slate-600">{excerpt}</p>
      ) : null}
    </div>
  );
}

export function InvoiceEscalationSection({
  invoiceId,
  state,
  draft,
}: {
  invoiceId: string;
  state: PaymentEscalationState;
  draft?: EmailDraft | null;
}) {
  const queryClient = useQueryClient();
  const stepLabel =
    state.total_levels > 0
      ? `Step ${state.current_level_sequence} of ${state.total_levels}`
      : "Escalated";

  const showDraft =
    draft &&
    (draft.requires_approval ||
      Boolean(draft.escalation_level_id) ||
      state.pending_approval);

  const invalidateEscalation = () => {
    void queryClient.invalidateQueries({
      queryKey: escalationKeys.payment(invoiceId),
    });
  };

  return (
    <section className="rounded-xl border border-indigo-200 bg-white p-4 shadow-sm">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
        Escalation
      </h2>

      <div className="mt-3 space-y-1 text-sm text-slate-800">
        <p className="font-medium text-slate-900">
          {stepLabel} · Escalated since {displayISODate(state.escalated_at)}
          {state.entry_reason
            ? ` (${formatEntryReason(state.entry_reason)})`
            : ""}
        </p>
        {state.next_level_name && state.days_until_next_level != null ? (
          <p className="text-slate-600">
            Next step &ldquo;{state.next_level_name}&rdquo; in{" "}
            {state.days_until_next_level} days
          </p>
        ) : null}
        {state.pending_approval ? (
          <span className="inline-flex rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
            Awaiting manager approval
          </span>
        ) : null}
      </div>

      <div className="mt-4">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Level history
        </h3>
        <EscalationEventTimeline events={state.events} />
      </div>

      {showDraft && draft ? (
        <div className="mt-4 space-y-3">
          <EscalationDraftPreview draft={draft} />
          <EscalationApprovalBanner
            draft={draft}
            invoiceId={invoiceId}
            onUpdated={invalidateEscalation}
          />
        </div>
      ) : null}
    </section>
  );
}
